/**
 * Wave 26.12 — Camera (standalone /camera route).
 *
 * Fast 1-tap workflow for technicians:
 *   1. Tap "Capture" → opens phone camera (or file picker on desktop).
 *   2. Photo is uploaded to the gallery immediately.
 *   3. (Optional) pick a Customer / JC / Ticket / Site to attach to.
 *
 * Multi-shot mode: snap several photos in a row without leaving the page;
 * the queue is shown as thumbnails, batch-assign at the end.
 *
 * Goal: ZERO friction. Technicians often have one hand free; everything
 * is at thumb-reach with big tap targets.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Camera as CameraIcon, X, Loader2, Check, ImagePlus, Search,
  Tag, Trash2, Sparkles, ArrowLeft, CheckCircle2, Receipt as ReceiptIcon,
  ScanLine,
} from "lucide-react";
import api from "../lib/api";
import { uploadFile, uploadUrl } from "../lib/uploads";
import { toast } from "../components/ui/sonner";
import { useBranding } from "../context/TenantBrandingContext";

const KINDS = [
  { kind: "customer", label: "Customer" },
  { kind: "jobcard", label: "Job Card" },
  { kind: "ticket", label: "Ticket" },
  { kind: "site", label: "Site" },
];

export default function Camera() {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Wave 27.0 — expense mode submits each upload to /api/expenses/scan
  // for OCR instead of going through the regular gallery assign flow.
  const expenseMode = searchParams.get("expense") === "1";
  const expenseProjectId = searchParams.get("project") || null;

  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const [queue, setQueue] = useState([]);   // [{id, status, upload, file, name, expenseId?}]
  const [busy, setBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const ingest = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    setBusy(true);
    const newItems = arr.map((f) => ({
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      name: f.name || "Photo.jpg",
      status: "uploading",
      upload: null,
      expenseId: null,
      previewUrl: URL.createObjectURL(f),
    }));
    setQueue((q) => [...newItems, ...q]);

    // Upload sequentially so we don't stampede the upload manager on mobile.
    for (const it of newItems) {
      try {
        const up = await uploadFile(it.file, { label: it.name });
        // Default: mark done after upload.
        setQueue((q) => q.map((x) => x.id === it.id ? { ...x, status: "done", upload: up } : x));
        // Expense mode: kick off OCR scan
        if (expenseMode) {
          try {
            const { data } = await api.post("/expenses/scan", {
              upload_id: up.id,
              project_id: expenseProjectId,
            });
            setQueue((q) => q.map((x) => x.id === it.id ? { ...x, status: "scanning", expenseId: data.id } : x));
          } catch (err) {
            setQueue((q) => q.map((x) => x.id === it.id ? { ...x, status: "scan_failed" } : x));
          }
        }
      } catch (e) {
        setQueue((q) => q.map((x) => x.id === it.id ? { ...x, status: "failed" } : x));
      }
    }
    setBusy(false);
  };

  const removeItem = (id) => {
    setQueue((q) => {
      const item = q.find((x) => x.id === id);
      if (item?.previewUrl) try { URL.revokeObjectURL(item.previewUrl); } catch (_e) {}
      return q.filter((x) => x.id !== id);
    });
  };

  // Cleanup blob URLs on unmount.
  useEffect(() => () => {
    queue.forEach((q) => { if (q.previewUrl) try { URL.revokeObjectURL(q.previewUrl); } catch (_e) {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doneCount = queue.filter((q) => q.status === "done").length;
  const uploadingCount = queue.filter((q) => q.status === "uploading").length;
  const failedCount = queue.filter((q) => q.status === "failed").length;
  const uploadedIds = queue.filter((q) => q.status === "done" && q.upload?.id).map((q) => q.upload.id);

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="camera-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hv-btn hv-btn-ghost text-xs shrink-0"
            data-testid="camera-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-bold truncate inline-flex items-center gap-2">
              {expenseMode ? <ReceiptIcon className="w-6 h-6 text-[#F59E0B]" /> : null}
              {expenseMode ? "Capture receipt" : "Camera"}
            </h1>
            <p className="text-xs text-[#647087] mt-0.5 truncate">
              {expenseMode
                ? "Snap a supplier receipt — GPT-5.2 Vision extracts items, totals and supplier for your manager to review."
                : `Snap and upload. Each photo gets auto-described by ${aiName} Vision.`}
            </p>
          </div>
        </div>
        {expenseMode ? (
          <Link to="/expenses/review" className="hv-btn hv-btn-ghost text-xs" data-testid="camera-open-review">
            Review queue
          </Link>
        ) : (
          <Link to="/gallery" className="hv-btn hv-btn-ghost text-xs" data-testid="camera-open-gallery">
            Gallery
          </Link>
        )}
      </div>

      {expenseMode && (
        <div className="hv-card !p-2 text-[11px] text-amber-200 bg-amber-500/[0.04] border-amber-400/30 flex items-start gap-2" data-testid="camera-expense-banner">
          <ScanLine className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            <strong className="text-amber-300">Receipt mode.</strong> Every photo you take is sent for OCR and lands in the Expense Review queue. Your manager will confirm the supplier, project and category before it counts.
            {expenseProjectId && <span> Linked to project <span className="font-mono">{expenseProjectId.slice(0, 8)}…</span></span>}
          </div>
        </div>
      )}

      {/* Capture controls */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <label
          className="hv-card !p-0 cursor-pointer hover:border-[#1FB6C1]/60 transition-colors flex flex-col items-center justify-center gap-2 h-32 sm:h-40 bg-gradient-to-br from-[#1FB6C1]/20 to-[#1FB6C1]/5"
          data-testid="camera-capture-tile"
          title="Open the device camera"
        >
          <CameraIcon className="w-10 h-10 text-[#1FB6C1]" />
          <span className="font-display font-bold text-sm">Capture</span>
          <span className="text-[10px] text-[#647087]">Use device camera</span>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            data-testid="camera-capture-input"
            onChange={(e) => { ingest(e.target.files); e.target.value = ""; }}
          />
        </label>
        <label
          className="hv-card !p-0 cursor-pointer hover:border-[#1FB6C1]/60 transition-colors flex flex-col items-center justify-center gap-2 h-32 sm:h-40"
          data-testid="camera-library-tile"
          title="Pick existing photos from your device"
        >
          <ImagePlus className="w-10 h-10 text-[#A78BFA]" />
          <span className="font-display font-bold text-sm">From Library</span>
          <span className="text-[10px] text-[#647087]">Pick existing photos</span>
          <input
            ref={libraryInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            data-testid="camera-library-input"
            onChange={(e) => { ingest(e.target.files); e.target.value = ""; }}
          />
        </label>
      </div>

      {/* Status pill */}
      {queue.length > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="camera-status-row">
          <div className="flex items-center gap-2 text-xs">
            {uploadingCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1FB6C1]/15 border border-[#1FB6C1]/30 text-[#1FB6C1]">
                <Loader2 className="w-3 h-3 animate-spin" /> Uploading {uploadingCount}
              </span>
            )}
            {doneCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400" data-testid="camera-status-done">
                <CheckCircle2 className="w-3 h-3" /> {doneCount} uploaded
              </span>
            )}
            {failedCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-400">
                <X className="w-3 h-3" /> {failedCount} failed
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAssignOpen(true)}
              disabled={uploadedIds.length === 0 || busy || expenseMode}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid="camera-assign-btn"
            >
              <Tag className="w-3.5 h-3.5" /> {expenseMode ? "Auto-routed" : `Assign (${uploadedIds.length})`}
            </button>
          </div>
        </div>
      )}

      {/* Queue thumbnails */}
      {queue.length > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5" data-testid="camera-queue">
          {queue.map((it) => (
            <CameraThumb key={it.id} item={it} onRemove={() => removeItem(it.id)} />
          ))}
        </div>
      ) : (
        <div className="hv-card p-8 text-center text-[#647087] text-sm" data-testid="camera-empty">
          <Sparkles className="w-7 h-7 mx-auto mb-2 opacity-40" />
          <div className="font-medium">Nothing captured yet.</div>
          <div className="text-xs mt-1">Tap Capture above — uploads happen automatically.</div>
        </div>
      )}

      {/* Tip */}
      <div className="hv-card !p-3 text-[11px] text-[#a0aabf] border-[#1FB6C1]/15 bg-[#1FB6C1]/[0.04]">
        {expenseMode ? (
          <>
            <strong className="text-[#F59E0B]">Tip:</strong> Take a clear, well-lit shot of the whole receipt including the total. Hidro will guess the supplier and category — your manager confirms at <Link to="/expenses/review" className="underline">Expense review</Link>.
          </>
        ) : (
          <>
            <strong className="text-[#1FB6C1]">Tip:</strong> Photos land in <Link to="/gallery" className="underline">Gallery</Link> under <em>Unassigned</em> until you attach them. Hidro auto-tags every shot, so a search like <em>"leaking pump"</em> later will find them.
          </>
        )}
      </div>

      {assignOpen && (
        <AssignDrawer
          uploadIds={uploadedIds}
          onClose={() => setAssignOpen(false)}
          onDone={(n) => {
            toast.success(`${n} photo${n === 1 ? "" : "s"} attached.`);
            setAssignOpen(false);
            setQueue([]);
          }}
        />
      )}
    </div>
  );
}

function CameraThumb({ item, onRemove }) {
  const previewSrc = item.upload
    ? uploadUrl(item.upload.id)
    : item.previewUrl;

  return (
    <div className="relative aspect-square rounded overflow-hidden bg-black/40 border border-white/5 group">
      {previewSrc ? (
        <img
          src={previewSrc}
          alt={item.name}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#647087]">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )}
      {/* Status overlay */}
      <div className="absolute inset-0 flex items-end justify-between p-1 pointer-events-none">
        {item.status === "uploading" && (
          <span className="text-[8px] uppercase px-1 py-0.5 rounded bg-[#1FB6C1] text-[#0a0b0e] font-bold inline-flex items-center gap-0.5">
            <Loader2 className="w-2 h-2 animate-spin" /> Up
          </span>
        )}
        {item.status === "scanning" && (
          <span className="text-[8px] uppercase px-1 py-0.5 rounded bg-[#F59E0B] text-[#0a0b0e] font-bold inline-flex items-center gap-0.5">
            <Loader2 className="w-2 h-2 animate-spin" /> OCR
          </span>
        )}
        {item.status === "done" && (
          <span className="text-[8px] uppercase px-1 py-0.5 rounded bg-emerald-500 text-[#0a0b0e] font-bold inline-flex items-center gap-0.5">
            <Check className="w-2 h-2" /> Ok
          </span>
        )}
        {(item.status === "failed" || item.status === "scan_failed") && (
          <span className="text-[8px] uppercase px-1 py-0.5 rounded bg-rose-500 text-white font-bold">Fail</span>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 hover:bg-rose-500 text-white opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center"
        aria-label="Remove"
        data-testid={`camera-remove-${item.id}`}
      >
        <Trash2 className="w-2.5 h-2.5" />
      </button>
    </div>
  );
}

function AssignDrawer({ uploadIds, onClose, onDone }) {
  const [kind, setKind] = useState("jobcard");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        let endpoint = "/customers";
        let params = { q: query, limit: 12 };
        if (kind === "jobcard") { endpoint = "/jobcards"; params = { search: query, limit: 12 }; }
        else if (kind === "ticket") { endpoint = "/tickets"; params = { q: query, limit: 12 }; }
        else if (kind === "site") { endpoint = "/sites"; params = { q: query, limit: 12 }; }
        const { data } = await api.get(endpoint, { params, signal: ctl.signal });
        const arr = Array.isArray(data) ? data : (data?.items || []);
        setResults(arr.slice(0, 12));
      } catch (e) {
        if (e?.name !== "CanceledError") setResults([]);
      } finally { setSearching(false); }
    }, 250);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [query, kind]);

  const labelFor = (item) => {
    if (kind === "customer") return item.name || item.trading_name || item.id;
    if (kind === "jobcard") return `${item.ref || item.id?.slice(0, 8)} — ${item.title || ""}`.trim();
    if (kind === "ticket") return `${item.ref || item.id?.slice(0, 8)} — ${item.subject || item.title || ""}`.trim();
    if (kind === "site") return item.label || item.name || item.id;
    return item.id;
  };

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/gallery/bulk/assign", {
        upload_ids: uploadIds, kind, id: selected.id,
      });
      onDone(data.assigned || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-[#0e1015] border border-white/10 rounded-t-2xl sm:rounded-lg w-full sm:max-w-md p-4 max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="camera-assign-drawer"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display font-bold">Attach {uploadIds.length} photo{uploadIds.length === 1 ? "" : "s"}</div>
            <div className="text-[11px] text-[#647087]">Pick where these belong.</div>
          </div>
          <button onClick={onClose} className="text-[#647087] hover:text-white" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-1 mb-3 flex-wrap">
          {KINDS.map((k) => (
            <button
              key={k.kind}
              type="button"
              onClick={() => { setKind(k.kind); setSelected(null); setResults([]); setQuery(""); }}
              className={`text-[11px] px-2 py-1 rounded border ${
                kind === k.kind ? "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]" : "border-white/15 text-[#a0aabf]"
              }`}
              data-testid={`camera-assign-kind-${k.kind}`}
            >
              {k.label}
            </button>
          ))}
        </div>
        <div className="relative mb-2">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
            placeholder={`Search ${kind === "customer" ? "customers" : `${kind}s`}…`}
            className="hv-input text-xs pl-8 pr-7 w-full"
            data-testid="camera-assign-search-input"
            autoFocus
          />
          {searching && <Loader2 className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-[#647087]" />}
        </div>
        <div className="space-y-1 mb-3 max-h-72 overflow-y-auto">
          {results.length === 0 && query && !searching && (
            <div className="text-[11px] text-[#647087] py-4 text-center">No matches.</div>
          )}
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelected(r)}
              className={`w-full text-left text-xs p-2 rounded border ${
                selected?.id === r.id ? "border-[#1FB6C1] bg-[#1FB6C1]/10" : "border-white/10 hover:bg-white/[0.04]"
              }`}
              data-testid={`camera-assign-result-${r.id}`}
            >
              <div className="font-medium text-white">{labelFor(r)}</div>
              {r.customer_name && r.customer_name !== labelFor(r) && (
                <div className="text-[10px] text-[#647087]">{r.customer_name}</div>
              )}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-white/5 pt-3">
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="camera-assign-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!selected || busy}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="camera-assign-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
            Attach
          </button>
        </div>
      </div>
    </div>
  );
}
